import React, { type FC, useState, useCallback, useEffect } from "react";
import { openInvoice } from "@telegram-apps/sdk";
import { Modal } from "@telegram-apps/telegram-ui";
import { type BookInfo, type BookIdEnum } from "../../handlers/types";
import { useI18n } from "../../utils/i18n/I18nProvider";
import ApiService from "../../handlers/apiService";
import StarIcon from "../Shop/StarIcon";
import { TUIModalHeader } from "../TUIModalHeader/TUIModalHeader";
import "./BookPromoModal.css";

interface BookPromoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseSuccess?: () => void;
}

function getBookCardRarityClass(book: BookInfo): string {
  if (!book.rarity) return "";
  return `book-promo-card--${book.rarity.toLowerCase()}`;
}

export const BookPromoModal: FC<BookPromoModalProps> = ({
  isOpen,
  onClose,
  onPurchaseSuccess,
}) => {
  const { t } = useI18n();
  const [isPurchasing, setIsPurchasing] = useState<string | null>(null);
  const [books, setBooks] = useState<BookInfo[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBooks = useCallback(async (): Promise<BookInfo[]> => {
    try {
      const api = ApiService.getInstance();
      const response = await api.api.purchasesList();
      const limitedAvailableBooks = (response?.books ?? []).filter(
        (book) => book.isLimited && book.isAvailable && !book.isOwned
      );
      setBooks(limitedAvailableBooks);
      return limitedAvailableBooks;
    } catch (error) {
      console.error("Failed to fetch books:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch books on open
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      void fetchBooks();
    }
  }, [isOpen, fetchBooks]);

  const purchaseBook = useCallback(
    async (bookId: BookIdEnum) => {
      setIsPurchasing(bookId);

      try {
        const response = await ApiService.getInstance().api.purchasesBuyCreate({
          bookId,
        });

        const { invoiceUrl } = response;

        if (invoiceUrl && openInvoice.isAvailable()) {
          const status = await openInvoice(invoiceUrl, "url");
          if (status !== "paid") {
            console.error("Payment not completed");
          } else {
            // Wait for 1 second before updating
            await new Promise((resolve) => setTimeout(resolve, 1000));
            // Refresh available books after successful purchase
            const remaining = await fetchBooks();
            // Keep modal open if there are still books available; close otherwise
            if (remaining.length === 0) {
              onPurchaseSuccess?.();
              onClose();
            }
          }
        } else {
          console.error("Invoice URL is not available");
        }
      } catch (err) {
        console.error("Failed to purchase book:", err);
      } finally {
        setIsPurchasing(null);
      }
    },
    [fetchBooks, onClose, onPurchaseSuccess]
  );

  if (loading) {
    return null;
  }

  if (books.length === 0) {
    return null;
  }

  return (
    <Modal
      open={isOpen}
      header={<TUIModalHeader />}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      className="book-promo-modal"
    >
      <div className="book-promo">
        <div className="book-promo__header">
          <h2 className="book-promo__title">
            {t("toolkit.books.promo.title", {
              defaultValue: "Claim Your Limited‑Edition Magic Book",
            })}
          </h2>
        </div>
        <div className="book-promo__items">
          {books.map((book) => (
            <div
              key={book.id}
              className={`book-promo-card ${getBookCardRarityClass(book)} ${
                isPurchasing === book.id ? "book-promo-card--loading" : ""
              }`}
              onClick={() =>
                isPurchasing === null
                  ? purchaseBook(book.id as BookIdEnum)
                  : null
              }
              style={{
                cursor: isPurchasing === null ? "pointer" : "default",
              }}
            >
              <div className="book-promo-card__content">
                <div className="book-promo-card__title">
                  {t(book.nameKey, { defaultValue: book.id })}
                </div>
                <div className="book-promo-card__image">
                  <div className="book-promo-card__image-glow" />
                  <img
                    src={`/assets/img/books/${book.imageKey}.avif`}
                    alt={t(book.nameKey, { defaultValue: book.id })}
                    className="book-promo-card__book-img"
                  />
                </div>

                {book.rarity && (
                  <div className="book-promo-card__rarity">
                    {t(`rarity.${book.rarity.toLowerCase()}`, {
                      defaultValue: book.rarity,
                    })}
                  </div>
                )}
                <div className="book-promo-card__description">
                  +{book.manaGenerationIncrement}{" "}
                  {t("toolkit.mana.generation_rate", {
                    defaultValue: "mana per day",
                  })}
                </div>
                {book.isLimited && book.maxCopies !== undefined && (
                  <>
                    {book.remainingCopies !== undefined &&
                      book.remainingCopies > 0 && (
                        <div className="book-promo-card__remaining">
                          <div className="book-promo-card__remaining-line">
                            {t("toolkit.books.promo.onlyRemainingLeft", {
                              defaultValue: "Only {{count}} left",
                              count: book.remainingCopies,
                            })}
                          </div>
                          <div className="book-promo-card__remaining-line">
                            {t("toolkit.books.promo.onlyRemainingCta", {
                              defaultValue: "Don’t miss out",
                            })}
                          </div>
                        </div>
                      )}
                  </>
                )}
                <div className="book-promo-card__price">
                  {isPurchasing === book.id ? (
                    <span className="book-promo-card__loader"></span>
                  ) : (
                    <span>{book.priceInStars}</span>
                  )}
                  {isPurchasing !== book.id && <StarIcon />}
                </div>
                {/* CTA button removed to reduce redundancy; price and entire card remain clickable */}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};
